import { jsPDF } from 'jspdf';
import { applyPlugin } from 'jspdf-autotable';
import { generateReceiptHeader, generateReceiptFooter, getCollegeInfo } from './receiptHeaderUtils';
import { ensureRupeeFont, formatINR } from './jspdfFonts';
import { formatDate } from '../utils/dateTimeUtils';
applyPlugin(jsPDF);

export const generateOtherFeeReceipt = async (paymentData, collegeInfo = null) => {
  const doc = new jsPDF();

  const resolvedFont = await ensureRupeeFont(doc);
  const CURRENCY = resolvedFont === 'FreeSans' ? '\u20B9' : 'Rs.';
  // Get college info - prefer parameter over localStorage
  if (!collegeInfo) {
    collegeInfo = getCollegeInfo() || {};
    console.warn('generateOtherFeeReceipt: collegeInfo not passed, using localStorage fallback. This may cause tenant isolation issues.');
  }
  let yPos = await generateReceiptHeader(doc, collegeInfo, 'OTHER FEE PAYMENT RECEIPT');

  // Receipt Number and Date
  doc.setFontSize(10);
  doc.setFont(resolvedFont, 'bold');
  doc.text('Receipt No:', 14, yPos);
  doc.setFont(resolvedFont, 'normal');
  doc.text(paymentData.receipt_number || 'N/A', 45, yPos);
  
  doc.setFont(resolvedFont, 'bold');
  doc.text('Date:', 140, yPos);
  doc.setFont(resolvedFont, 'normal');
  doc.text(formatDate(paymentData.payment_date || new Date()), 155, yPos);

  // Student Information
  yPos += 12;
  doc.setFontSize(12);
  doc.setFont(resolvedFont, 'bold');
  doc.text('Student Information', 14, yPos);
  
  yPos += 8;
  doc.setFontSize(10);
  
  const studentInfo = [
    ['Student Name', paymentData.student_name || 'N/A'],
    ['Admission Number', paymentData.admission_number || 'N/A'],
    ['Mobile', paymentData.mobile || 'N/A'],
    ['Email', paymentData.email || 'N/A'],
    ['Course', paymentData.course || 'N/A'],
    ['Session', paymentData.session || 'N/A']
  ];

  doc.autoTable({
    startY: yPos,
    head: [],
    body: studentInfo,
    theme: 'plain',
    styles: { fontSize: 10, cellPadding: 2 , font: resolvedFont },
    columnStyles: {
      0: { cellWidth: 50, fontStyle: 'bold', textColor: [100, 100, 100] },
      1: { cellWidth: 130, fontStyle: 'normal' }
    }
  });

  yPos = doc.lastAutoTable.finalY + 10;

  // Fee Details
  doc.setFontSize(12);
  doc.setFont(resolvedFont, 'bold');
  doc.text('Fee Details', 14, yPos);
  
  yPos += 8;
  
  const feeDetails = [
    ['Other Fee Name', paymentData.other_fee_name || 'N/A'],
    ['Semester', paymentData.semester || 'N/A'],
    ['Type', paymentData.type || 'N/A']
  ];

  doc.autoTable({
    startY: yPos,
    head: [],
    body: feeDetails,
    theme: 'plain',
    styles: { fontSize: 10, cellPadding: 2 , font: resolvedFont },
    columnStyles: {
      0: { cellWidth: 50, fontStyle: 'bold', textColor: [100, 100, 100] },
      1: { cellWidth: 130, fontStyle: 'normal' }
    }
  });

  yPos = doc.lastAutoTable.finalY + 10;

  // Payment Summary
  doc.setFontSize(12);
  doc.setFont(resolvedFont, 'bold');
  doc.text('Payment Summary', 14, yPos);
  
  yPos += 2;

  const feeAmount = parseFloat(paymentData.amount || 0);
  const finePaid = parseFloat(paymentData.fine_paid || 0);
  const totalAmount = feeAmount + finePaid;

  const summaryData = [
    ['Other Fee Amount', `${CURRENCY} ${formatINR(feeAmount)}`],
    ['Late Fine (if any)', `${CURRENCY} ${formatINR(finePaid)}`],
    ['Total Amount Paid', `${CURRENCY} ${formatINR(totalAmount)}`]
  ];

  doc.autoTable({
    startY: yPos,
    head: [['Description', 'Amount']],
    body: summaryData,
    theme: 'striped',
    headStyles: { fillColor: [41, 128, 185], textColor: 255 },
    styles: { fontSize: 10 , font: resolvedFont },
    columnStyles: {
      0: { cellWidth: 100 },
      1: { cellWidth: 70, halign: 'right', fontStyle: 'bold' }
    }
  });

  yPos = doc.lastAutoTable.finalY + 10;

  // Payment Details
  doc.setFontSize(12);
  doc.setFont(resolvedFont, 'bold');
  doc.text('Payment Details', 14, yPos);
  
  yPos += 8;
  
  const paymentDetails = [
    ['Payment Mode', paymentData.payment_mode || 'N/A'],
    ['Payment Date', paymentData.payment_date ? formatDate(paymentData.payment_date) : 'N/A'],
    ['Collected By', paymentData.collected_by || 'Admin']
  ];

  doc.autoTable({
    startY: yPos,
    head: [],
    body: paymentDetails,
    theme: 'plain',
    styles: { fontSize: 10, cellPadding: 2 , font: resolvedFont },
    columnStyles: {
      0: { cellWidth: 50, fontStyle: 'bold', textColor: [100, 100, 100] },
      1: { cellWidth: 130, fontStyle: 'normal' }
    }
  });

  // Processed By Info
  yPos = doc.lastAutoTable.finalY + 10;
  if (paymentData.payment_created_by_name || paymentData.created_by_name) {
    doc.setFontSize(9);
    doc.setFont(resolvedFont, 'bold');
    doc.text('Processed By:', 14, yPos);
    doc.setFont(resolvedFont, 'normal');
    const userName = paymentData.payment_created_by_name || paymentData.created_by_name || 'Admin';
    const userRole = paymentData.payment_created_by_role || paymentData.created_by_role || '';
    const userEmail = paymentData.payment_created_by_email || paymentData.created_by_email || '';
    doc.text(`${userName}${userRole ? ' (' + userRole + ')' : ''}`, 14, yPos + 6);
    if (userEmail) {
      doc.text(userEmail, 14, yPos + 12);
    }
  }

  // Footer
  const pageHeight = doc.internal.pageSize.height;
  doc.setFontSize(8);
  doc.setFont(resolvedFont, 'italic');
  doc.text('This is a computer-generated receipt and does not require a signature.', 105, pageHeight - 20, { align: 'center' });
  doc.text('Thank you for your payment!', 105, pageHeight - 15, { align: 'center' });

  // Save the PDF
  doc.save(`Other_Fee_Receipt_${paymentData.receipt_number || 'N/A'}.pdf`);

  return doc;
};

// Function to view receipt in new tab (without downloading)
export const viewOtherFeeReceipt = async (paymentData, collegeInfo = null) => {
  const doc = new jsPDF();
  const resolvedFont = await ensureRupeeFont(doc);
  const CURRENCY = resolvedFont === 'FreeSans' ? '\u20B9' : 'Rs.';

  // Get college info - prefer parameter over localStorage
  if (!collegeInfo) {
    collegeInfo = getCollegeInfo() || {};
    console.warn('viewOtherFeeReceipt: collegeInfo not passed, using localStorage fallback. This may cause tenant isolation issues.');
  }
  
  // Generate header with tenant branding
  let yPos = await generateReceiptHeader(doc, collegeInfo, 'OTHER FEE PAYMENT RECEIPT');

  // Receipt Number and Date
  doc.setFontSize(10);
  doc.setFont(resolvedFont, 'bold');
  doc.text('Receipt No:', 14, yPos);
  doc.setFont(resolvedFont, 'normal');
  doc.text(paymentData.receipt_number || 'N/A', 45, yPos);
  
  doc.setFont(resolvedFont, 'bold');
  doc.text('Date:', 140, yPos);
  doc.setFont(resolvedFont, 'normal');
  doc.text(formatDate(paymentData.payment_date || new Date()), 155, yPos);

  // Student Information
  yPos += 15;
  doc.setFontSize(12);
  doc.setFont(resolvedFont, 'bold');
  doc.text('Student Information', 14, yPos);
  
  yPos += 8;
  doc.setFontSize(10);
  
  const studentInfo = [
    ['Student Name', paymentData.student_name || 'N/A'],
    ['Admission Number', paymentData.admission_number || 'N/A'],
    ['Mobile', paymentData.mobile || 'N/A'],
    ['Email', paymentData.email || 'N/A'],
    ['Course', paymentData.course || 'N/A'],
    ['Session', paymentData.session || 'N/A']
  ];

  doc.autoTable({
    startY: yPos,
    head: [],
    body: studentInfo,
    theme: 'plain',
    styles: { fontSize: 10, cellPadding: 2 , font: resolvedFont },
    columnStyles: {
      0: { cellWidth: 50, fontStyle: 'bold', textColor: [100, 100, 100] },
      1: { cellWidth: 130, fontStyle: 'normal' }
    }
  });

  yPos = doc.lastAutoTable.finalY + 10;

  // Fee Details
  doc.setFontSize(12);
  doc.setFont(resolvedFont, 'bold');
  doc.text('Fee Details', 14, yPos);
  
  yPos += 8;
  
  const feeDetails = [
    ['Other Fee Name', paymentData.other_fee_name || 'N/A'],
    ['Semester', paymentData.semester || 'N/A'],
    ['Type', paymentData.type || 'N/A']
  ];

  doc.autoTable({
    startY: yPos,
    head: [],
    body: feeDetails,
    theme: 'plain',
    styles: { fontSize: 10, cellPadding: 2 , font: resolvedFont },
    columnStyles: {
      0: { cellWidth: 50, fontStyle: 'bold', textColor: [100, 100, 100] },
      1: { cellWidth: 130, fontStyle: 'normal' }
    }
  });

  yPos = doc.lastAutoTable.finalY + 10;

  // Payment Summary
  doc.setFontSize(12);
  doc.setFont(resolvedFont, 'bold');
  doc.text('Payment Summary', 14, yPos);
  
  yPos += 2;

  const feeAmount = parseFloat(paymentData.amount || 0);
  const finePaid = parseFloat(paymentData.fine_paid || 0);
  const totalAmount = feeAmount + finePaid;

  const summaryData = [
    ['Other Fee Amount', `${CURRENCY} ${formatINR(feeAmount)}`],
    ['Late Fine (if any)', `${CURRENCY} ${formatINR(finePaid)}`],
    ['Total Amount Paid', `${CURRENCY} ${formatINR(totalAmount)}`]
  ];

  doc.autoTable({
    startY: yPos,
    head: [['Description', 'Amount']],
    body: summaryData,
    theme: 'striped',
    headStyles: { fillColor: [41, 128, 185], textColor: 255 },
    styles: { fontSize: 10 , font: resolvedFont },
    columnStyles: {
      0: { cellWidth: 100 },
      1: { cellWidth: 70, halign: 'right', fontStyle: 'bold' }
    }
  });

  yPos = doc.lastAutoTable.finalY + 10;

  // Payment Details
  doc.setFontSize(12);
  doc.setFont(resolvedFont, 'bold');
  doc.text('Payment Details', 14, yPos);
  
  yPos += 8;
  
  const paymentDetails = [
    ['Payment Mode', paymentData.payment_mode || 'N/A'],
    ['Payment Date', paymentData.payment_date ? formatDate(paymentData.payment_date) : 'N/A'],
    ['Collected By', paymentData.collected_by || 'Admin']
  ];

  doc.autoTable({
    startY: yPos,
    head: [],
    body: paymentDetails,
    theme: 'plain',
    styles: { fontSize: 10, cellPadding: 2 , font: resolvedFont },
    columnStyles: {
      0: { cellWidth: 50, fontStyle: 'bold', textColor: [100, 100, 100] },
      1: { cellWidth: 130, fontStyle: 'normal' }
    }
  });

  // Processed By Info
  yPos = doc.lastAutoTable.finalY + 10;
  if (paymentData.payment_created_by_name || paymentData.created_by_name) {
    doc.setFontSize(9);
    doc.setFont(resolvedFont, 'bold');
    doc.text('Processed By:', 14, yPos);
    doc.setFont(resolvedFont, 'normal');
    const userName = paymentData.payment_created_by_name || paymentData.created_by_name || 'Admin';
    const userRole = paymentData.payment_created_by_role || paymentData.created_by_role || '';
    const userEmail = paymentData.payment_created_by_email || paymentData.created_by_email || '';
    doc.text(`${userName}${userRole ? ' (' + userRole + ')' : ''}`, 14, yPos + 6);
    if (userEmail) {
      doc.text(userEmail, 14, yPos + 12);
    }
  }

  // Footer
  const pageHeight = doc.internal.pageSize.height;
  doc.setFontSize(8);
  doc.setFont(resolvedFont, 'italic');
  doc.text('This is a computer-generated receipt and does not require a signature.', 105, pageHeight - 20, { align: 'center' });
  doc.text('Thank you for your payment!', 105, pageHeight - 15, { align: 'center' });

  // Open PDF in new window instead of downloading
  const pdfBlob = doc.output('blob');
  const pdfUrl = URL.createObjectURL(pdfBlob);
  window.open(pdfUrl, '_blank');

  return doc;
};
